import { URLParameters } from '@/app/common/urlParameters';

/**
 * The ?modal= stack: the ids of the modals that are open, outermost first. Modals keep it in the
 * url so the browser back button closes them one at a time (see useBrowserBackButton).
 */
export function getCurrentModals() {
  // MPH-965: parse the query string, not the whole href. URLSearchParams splits on '&', so with the
  // full href the first pair keeps the origin+path glued to its key ('https://host/path?modal') and
  // the modal stack is only found by accident, when some other parameter comes first in the url.
  const searchParams = new URLSearchParams(window.location.search);
  return searchParams.get(URLParameters.MODAL)?.split(',') ?? [];
}

// What is left of the stack the page arrived on, for modals that were not mounted to read it
// themselves. MPH-985: the restore used to read the url whenever a provider mounted, which is how a
// modal mounted much later — the flight details inside the change-flight drawer — found an id left
// over from the load and opened on its own. Taking the stack once, and spending it, is what stops
// that; PageWrapperClient, which strips ?modal= on mount, hands it over before stripping so the
// restore does not depend on which of the two effects runs first either.
// undefined = not captured yet; [] = captured and nothing (left) to restore.
let restorableModals: string[] | undefined;
let watchingHistoryEntries = false;

function captureFromCurrentUrl() {
  // Only the outermost modal is restorable. The ids below it belong to inner modals that are not
  // even mounted yet and whose data (a flight, a room) has not been fetched, so nothing here can
  // reopen them — the modals that are mounted keep reading the url on popstate as before.
  restorableModals = getCurrentModals().slice(0, 1);
}

export function captureInitialModalStack() {
  if (!watchingHistoryEntries) {
    watchingHistoryEntries = true;
    // Back and forward land on another history entry, which carries a stack of its own, and inside
    // the funnel that happens without a document load: PageWrapperClient swaps the page by pageType
    // (flight page <-> package review), so the page's modal providers remount with the stack of the
    // document long spent. Every entry the app lands on brings its own. (MPH-985)
    window.addEventListener('popstate', captureFromCurrentUrl);
  }
  if (restorableModals) return;
  captureFromCurrentUrl();
}

/**
 * Whether `modalId` is the modal a restore would reopen — the outermost one of the stack the page
 * arrived on — without claiming it. Lets that one instance, and only that one, wait for the cookie
 * banner before it takes the restore (MPH-1137); the stack is captured here if nobody has yet.
 */
export function peekRestorableModal(modalId: string) {
  captureInitialModalStack();
  return restorableModals?.[0] === modalId;
}

/**
 * Claims `modalId` for a restore, if it is the outermost modal of the stack the page arrived on.
 * Restoring is one-shot: once a modal has taken it, that stack is spent.
 */
export function takeRestorableModal(modalId: string) {
  captureInitialModalStack();
  if (restorableModals?.[0] !== modalId) return false;
  dropRestorableModals();
  return true;
}

/** The stack stops being restorable as soon as the user opens a modal themselves. */
export function dropRestorableModals() {
  restorableModals = [];
}

/** Tests only — the capture otherwise lives as long as the document. */
export function resetInitialModalStack() {
  restorableModals = undefined;
}
